// Telegram's date entity (`<tg-time unix="…" format="…">`, markdown `![text](tg://time?unix=…&format=…)`):
// `format` is `r` (relative), or any of `w` (weekday), `d`/`D` (short/long date), `t`/`T` (short/long time).

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
]

export function dateTimeOptions(format: string): Intl.DateTimeFormatOptions {
  const options: Intl.DateTimeFormatOptions = {}
  if (format.includes('w')) options.weekday = 'long'
  if (format.includes('D')) {
    options.year = 'numeric'
    options.month = 'long'
    options.day = 'numeric'
  } else if (format.includes('d')) {
    options.year = 'numeric'
    options.month = '2-digit'
    options.day = '2-digit'
  }
  if (format.includes('T')) {
    options.hour = '2-digit'
    options.minute = '2-digit'
    options.second = '2-digit'
  } else if (format.includes('t')) {
    options.hour = '2-digit'
    options.minute = '2-digit'
  }
  // Nothing recognised (or only a weekday): show a full date and time like Telegram's default.
  const hasDateOrTime = options.year || options.hour
  if (!hasDateOrTime) {
    options.year = 'numeric'
    options.month = 'long'
    options.day = 'numeric'
    options.hour = '2-digit'
    options.minute = '2-digit'
  }
  return options
}

export function formatRelative(unix: number, now: number, locale: string): string {
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const seconds = unix - now
  const absolute = Math.abs(seconds)
  for (const [unit, size] of RELATIVE_UNITS) {
    if (absolute >= size || unit === 'second') {
      return formatter.format(Math.trunc(seconds / size), unit)
    }
  }
  return formatter.format(0, 'second')
}

/**
 * The date as text in the viewer's timezone, or null for a unix time that is no date (the caller keeps
 * its fallback text then). `now` (seconds) and `timeZone` are injectable for tests.
 */
export function formatTgTime(
  unix: number,
  format: string,
  locale: string,
  now: number = Date.now() / 1000,
  timeZone?: string,
): string | null {
  const date = new Date(unix * 1000)
  if (!Number.isFinite(unix) || Number.isNaN(date.getTime())) return null
  try {
    if (format === 'r') return formatRelative(unix, now, locale)
    return new Intl.DateTimeFormat(locale, { ...dateTimeOptions(format), timeZone }).format(date)
  } catch {
    return null
  }
}
